import { create } from "zustand";
import { devtools } from "zustand/middleware";

import { createExperimentStorageAdapter } from "@func-core/core/data/adapters/experiment-storage-adapter";

export type ExperimentAssignment = {
  experimentId: string;
  variantId: string;
};

type ExperimentState = {
  assignments: Record<string, ExperimentAssignment>;
  getExperimentAssignment: (experimentId: string) => ExperimentAssignment | undefined;
  saveAssignment: (experimentId: string, variantId: string, isPreQuotes: boolean) => void;
  storeUserHash: (userKey: string, countryCodeIso: string) => void;
  getUserHash: () => string | undefined;
  storeAnonymousHash: (anonKey: string, countryCodeIso: string) => void;
  getAnonymousHash: () => string | undefined;
  clearAll: () => void;
};

export const useExperimentStore = create<ExperimentState>()(
  devtools(
    (set, get) => {
      const storageAdapter = createExperimentStorageAdapter();
      const storedAssignments = storageAdapter.getAllAssignments();

      return {
        assignments: storedAssignments,
        saveAssignment: (experimentId: string, variantId: string, isPreQuotes: boolean) => {
          storageAdapter.storeAssignment(experimentId, variantId, isPreQuotes);
          set(
            (state) => ({
              assignments: {
                ...state.assignments,
                [experimentId]: {
                  experimentId,
                  variantId,
                  isPreQuotes,
                },
              },
            }),
            false,
            `saveAssignment/${experimentId}`
          );
        },
        storeUserHash: (userKey: string, countryCodeIso: string) => {
          storageAdapter.storeUserHash(userKey, countryCodeIso);
        },
        getUserHash: () => {
          return storageAdapter.getUserHash();
        },
        storeAnonymousHash: (anonKey: string, countryCodeIso: string) => {
          storageAdapter.storeAnonymousHash(anonKey, countryCodeIso);
        },
        getAnonymousHash: () => {
          return storageAdapter.getAnonymousHash();
        },
        clearAll: () => {
          storageAdapter.clearAll();
          set(
            () => ({
              assignments: {},
            }),
            false,
            "clearAll"
          );
        },
        getExperimentAssignment: (experimentId: string) => {
          return get().assignments[experimentId];
        },
      };
    },
    {
      name: "Experiment Store",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);
